"use client"

import { cn, Popover, PopoverContent, PopoverTrigger } from "@heroui/react"
import { useTranslations } from "next-intl"
import { useRef, useState } from "react"
import { createPortal } from "react-dom"
import type { AgentSkill } from "@/apis/agent"
import { useSkills } from "./skillCatalog"
import { SkillStoreModal } from "./SkillStoreModal"

/** 技能名称/描述：优先前端 i18n（skills.<id>.*），无则回退目录自带英文 */
export function useSkillText() {
  const t = useTranslations("skills")
  return (skill: AgentSkill) => ({
    name: t.has(`${skill.id}.name` as any) ? t(`${skill.id}.name` as any) : skill.name,
    desc: t.has(`${skill.id}.desc` as any) ? t(`${skill.id}.desc` as any) : skill.description,
  })
}

/**
 * 技能 icon 兜底：后端 emoji 若因存储编码丢失会变成 "??"/"????"（非 utf8mb4），
 * 或为空，统一回退默认图标。
 */
export function normalizeSkillIcon(icon?: string): string {
  const trimmed = (icon ?? "").trim()
  if (!trimmed || /^[?？]+$/.test(trimmed)) return "✨"
  return trimmed
}

const SkillIcon = ({ icon, className }: { icon: string; className?: string }) => {
  if (/^https?:\/\//.test(icon)) {
    return <img src={icon} alt="" className={cn("rounded-lg object-cover", className)} />
  }
  return (
    <span
      className={cn(
        "grid place-content-center rounded-lg bg-theme-primary/[0.06] text-lg leading-none",
        className,
      )}
    >
      {normalizeSkillIcon(icon)}
    </span>
  )
}

/** 技能落点标签：Agent 技能会进入对话式创作 */
function TargetBadge({ target }: { target: AgentSkill["target"] }) {
  const t = useTranslations("generatePage")
  const label =
    target === "agent" || target === "drama"
      ? "Agent"
      : target === "video"
        ? t("video")
        : t("image")
  return (
    <span
      className={cn(
        "rounded px-1 text-[10px] font-medium capitalize leading-4",
        target === "agent" || target === "drama"
          ? "bg-purple-blue/10 text-purple-blue"
          : "bg-theme-primary/[0.06] text-theme-primary/50",
      )}
    >
      {label}
    </span>
  )
}

/** 单行技能项：图标（有封面用封面）+ 名称 + 落点标签 + 描述；悬停浮层实时预览封面/视频 */
export function SkillRow({
  skill,
  onSelect,
  className,
}: {
  skill: AgentSkill
  onSelect: (skill: AgentSkill) => void
  className?: string
}) {
  const text = useSkillText()(skill)
  const rowRef = useRef<HTMLButtonElement>(null)
  const [previewRect, setPreviewRect] = useState<DOMRect | null>(null)
  const hasPreview = !!(skill.cover || skill.video)

  return (
    <>
      <button
        ref={rowRef}
        type="button"
        onClick={() => onSelect(skill)}
        onMouseEnter={() => {
          if (hasPreview && rowRef.current) setPreviewRect(rowRef.current.getBoundingClientRect())
        }}
        onMouseLeave={() => setPreviewRect(null)}
        className={cn(
          "flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-theme-primary/5",
          className,
        )}
      >
        {skill.cover ? (
          <img src={skill.cover} alt="" className="size-9 shrink-0 rounded-lg object-cover" />
        ) : (
          <SkillIcon icon={skill.icon} className="size-9 shrink-0" />
        )}
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-1.5 text-sm font-medium text-theme-primary">
            <span className="truncate">{text.name}</span>
            <TargetBadge target={skill.target} />
          </span>
          <span className="line-clamp-2 text-xs leading-4 text-theme-primary/40">{text.desc}</span>
        </span>
      </button>

      {/* 悬停预览浮层：视频自动播放，无视频则大图 */}
      {previewRect &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: Math.max(8, previewRect.left - 296),
              top: Math.min(Math.max(8, previewRect.top - 60), window.innerHeight - 200),
              zIndex: 100,
            }}
            className="pointer-events-none w-[280px] overflow-hidden rounded-xl border border-theme-primary/10 bg-surface shadow-[0px_12px_40px_0px_rgba(0,0,0,0.25)]"
          >
            <div className="relative aspect-video bg-black">
              {skill.video ? (
                <video src={skill.video} muted loop playsInline autoPlay className="size-full object-cover" />
              ) : (
                skill.cover && <img src={skill.cover} alt="" className="size-full object-cover" />
              )}
            </div>
            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-theme-primary">{text.name}</p>
              <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-theme-primary/45">{text.desc}</p>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

const SkillsGlyph = ({ className }: { className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M8 1.6 9.4 5.4a1 1 0 0 0 .59.59L13.8 7.4a.64.64 0 0 1 0 1.2l-3.8 1.4a1 1 0 0 0-.59.59L8 14.4a.64.64 0 0 1-1.2 0L5.4 10.6a1 1 0 0 0-.59-.59L1 8.6a.64.64 0 0 1 0-1.2l3.8-1.4a1 1 0 0 0 .59-.59L6.8 1.6a.64.64 0 0 1 1.2 0Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path d="M12.8 1.2v2.6M11.5 2.5h2.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
)

/**
 * 技能菜单：输入工具栏上的「技能」入口，弹出技能列表；
 * 「更多」打开技能商店（封面/视频预览 + 创建技能）。
 * onSelect 由父级决定落点（填模板 / 进 Agent）。
 */
export function SkillsMenu({
  onSelect,
  triggerClassName,
  compact,
}: {
  onSelect: (skill: AgentSkill) => void
  triggerClassName?: string
  /** 移动端只显示图标 */
  compact?: boolean
}) {
  const t = useTranslations("generatePage")
  const { skills } = useSkills()
  const [open, setOpen] = useState(false)
  const [storeOpen, setStoreOpen] = useState(false)

  return (
    <>
      <Popover isOpen={open} onOpenChange={setOpen} placement="top-start" offset={8}>
        <PopoverTrigger>
          <button type="button" className={triggerClassName}>
            <SkillsGlyph className="size-4" />
            {!compact && <span>{t("skills")}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] max-w-[calc(100vw-32px)] items-stretch gap-0 rounded-2xl border-[0.5px] border-theme-primary/10 bg-surface p-0 shadow-[0px_8px_32px_0px_rgba(0,0,0,0.16)]">
          <div className="flex items-center justify-between px-4 pb-1 pt-3">
            <span className="text-sm font-semibold text-theme-primary">{t("skills")}</span>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setStoreOpen(true)
              }}
              className="flex items-center gap-0.5 text-xs text-theme-primary/50 transition-colors hover:text-theme-primary"
            >
              {t("skillMore")}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="m4.5 2.5 3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="flex max-h-[380px] flex-col overflow-y-auto p-1.5">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                onSelect={(s) => {
                  setOpen(false)
                  onSelect(s)
                }}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <SkillStoreModal isOpen={storeOpen} onOpenChange={setStoreOpen} onSelect={onSelect} />
    </>
  )
}
